import { CommonModule } from '@angular/common';
import {
  ProviderToken,
  ApplicationRef,
  Component,
  computed,
  Inject,
  inject,
  OnInit,
  Optional,
  Type,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BlockingLoadingSpinnerComponent, RefreshService } from '@softline/application';
import { UserInfoService } from '@softline/auth';
import { Store } from '@softline/core';
import {
  IconComponent,
  IsDefinedNotEmptyPipe,
  LegacyModalOption,
} from '@softline/ui-core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT,
  SOFTLINE_FEATURE_UTILITIES_FIRMA,
  SOFTLINE_FEATURE_UTILITIES_VKFORGS,
} from '@softapps/core/context';
import { FirmaStore } from '@softapps/core/context';
import { PatchContextService } from '@softapps/core/context';
import { VkforgStore } from '@softapps/core/context';
import { Vkforg } from '@softapps/core/context';
import { SoftAppsUserInfo } from '@softapps/core';

@Component({
  selector: 'vkforg-context-widget',
  imports: [
    CommonModule,
    BlockingLoadingSpinnerComponent,
    IsDefinedNotEmptyPipe,
    IconComponent,
  ],
  templateUrl: './vkforg-context-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vkforg-context-widget.component.css'],
})
export class VkforgContextWidgetComponent implements OnInit {
  private readonly userInfo = inject<UserInfoService<SoftAppsUserInfo>>(UserInfoService);
  private readonly store = inject(Store);

  readonly vkforgs$ = this.store.observe(
    SOFTLINE_FEATURE_UTILITIES_VKFORGS,
    VkforgStore.getters.all
  );

  readonly firmen$ = this.store.observe(
    SOFTLINE_FEATURE_UTILITIES_FIRMA,
    FirmaStore.getters.all
  );

  readonly context$ = toObservable(computed(() => this.userInfo.value()));

  readonly vkforg$ = combineLatest([this.vkforgs$, this.context$]).pipe(
    map(([o, context]) => o.find((v) => v.id === context?.vkforgid) ?? o[0])
  );

  readonly firma$ = combineLatest([this.firmen$, this.vkforg$]).pipe(
    map(([firmen, vkforg]) => firmen.find((o) => o?.id === vkforg?.idfirma))
  );

  readonly user$ = toObservable(computed(() => this.userInfo.value()));

  constructor(
    private refreshService: RefreshService,
    readonly patchContextService: PatchContextService,
    @Optional()
    @Inject(SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT)
    public components?: Type<Component>[]
  ) {
    // route-level multi providers (shell-scoped module globals) SHADOW root
    // registrations — union both injector levels (dedupe by reference)
    const rootComponents =
      inject(ApplicationRef).injector.get(
        // legacy string token — cast for the InjectOptions overload
        SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT as unknown as ProviderToken<
          Type<Component>[] | null
        >,
        null,
        { optional: true }
      ) ?? [];
    this.components = [...new Set([...rootComponents, ...(this.components ?? [])])];
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([
      await this.store.dispatch(
        SOFTLINE_FEATURE_UTILITIES_VKFORGS,
        VkforgStore.actions.loadMany,
        { clear: true }
      ),
      await this.store.dispatch(
        SOFTLINE_FEATURE_UTILITIES_FIRMA,
        FirmaStore.actions.loadMany,
        { clear: true }
      ),
    ]);
  }

  async onChangeAccount(vkforgs: Vkforg[]): Promise<void> {
    const options = vkforgs.map<LegacyModalOption<string>>((o) => ({
      value: o?.id + '',
      label:
        o?.kurzbezeichnung?.length < 1
          ? o?.vkforg
          : ((o?.kurzbezeichnung || o?.vkforg) ?? ''),
    }));

    if (this.components && this.components?.length > 1) {
      options.push({
        value: 'FIRMENWECHSEL_OEFFNEN',
        label: '#UTILITIES.CONTEXT.DETAIL_CONTEXT_SETTINGS',
        icon: 'fa-regular fa-user-gear',
      });
    }

    await this.patchContextService.patchContextOptionsDialog<string>({
      title: 'Verkaufsorganisation auswählen',
      options,
      editContextDialogOptionValue: 'FIRMENWECHSEL_OEFFNEN',
      setOnSelect: (selectedVkforgId) => {
        const vkforg = this.store.get(
          SOFTLINE_FEATURE_UTILITIES_VKFORGS,
          VkforgStore.getters.entity,
          +selectedVkforgId
        );

        if (!vkforg) return null;

        return {
          vkforgid: +selectedVkforgId,
          firmaid: vkforg.idfirma,
        };
      },
    });
  }
}
